import type * as React from 'react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type Tone = NonNullable<BadgeProps['variant']>;

const TONES: Record<Exclude<Tone, 'default'>, readonly string[]> = {
  success: [
    'COMPLETED', 'ACTIVE', 'ACCEPTED', 'SUCCESS', 'PAID', 'RECEIVED', 'POSTED',
    'SENT', 'APPROVED', 'DELIVERED', 'REDEEMED', 'CLEARED', 'AVAILABLE',
    'CONFIRMED', 'CLOCK_IN', 'LIVE', 'GENERATED', 'SIGNED',
  ],
  danger: [
    'VOIDED', 'FAILED', 'REJECTED', 'SUSPENDED', 'CANCELLED', 'CANCELED',
    'EXPIRED', 'BOUNCED', 'OCCUPIED', 'NO_SHOW', 'OVERDUE',
  ],
  warning: [
    'PENDING', 'PROCESSING', 'PARTIAL', 'QUEUED', 'PENDING_APPROVAL',
    'IN_PROGRESS', 'OPEN', 'IN_TRANSIT', 'STAGING', 'HELD', 'PAST_DUE',
  ],
  info: [
    'REFUNDED', 'WAIVED', 'TRIAL', 'ORDERED', 'SHIPPED', 'DEPOSITED',
    'BILLED', 'RESERVED', 'WHOLESALE', 'DINE-IN', 'DINE_IN',
  ],
  secondary: [
    'SKIPPED', 'ARCHIVED', 'DRAFT', 'INACTIVE', 'OFF', 'CLOCK_OUT', 'REGULAR',
  ],
  primary: [],
  outline: [],
};

/** Full-fill class sets (high-visibility contexts, e.g. KDS). Semantic `*-foreground` on `bg-*`. */
const SOLID_CLASSES: Record<Tone, string> = {
  // Brand primary: ~4.1:1 in dark mode (borderline). Intentionally unchanged — global
  // --primary/--primary-foreground drive buttons/brand app-wide.
  default: 'bg-primary text-primary-foreground',
  primary: 'bg-primary text-primary-foreground',
  secondary: 'bg-secondary text-secondary-foreground',
  success: 'bg-success text-success-foreground',
  warning: 'bg-warning text-warning-foreground',
  danger: 'bg-destructive text-destructive-foreground',
  // bg-info is ~2.8:1 with its foreground in light; --info-text/--info-foreground are
  // opposite-lightness in both themes, so this pairing is AA in light and dark.
  info: 'bg-info-text text-info-foreground',
  outline: 'bg-foreground text-background',
};

const LABELS: Record<string, string> = {
  COMPLETED: 'Completed',
  ACTIVE: 'Active',
  ACCEPTED: 'Accepted',
  SUCCESS: 'Success',
  PAID: 'Paid',
  RECEIVED: 'Received',
  POSTED: 'Posted',
  VOIDED: 'Voided',
  FAILED: 'Failed',
  REJECTED: 'Rejected',
  SUSPENDED: 'Suspended',
  CANCELLED: 'Cancelled',
  INACTIVE: 'Inactive',
  PENDING: 'Pending',
  PROCESSING: 'Processing',
  PARTIAL: 'Partial',
  QUEUED: 'Queued',
  SENT: 'Sent',
  GENERATED: 'Generated',
  SKIPPED: 'Skipped',
  ARCHIVED: 'Archived',
  HELD: 'Held',
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  REFUNDED: 'Refunded',
  WAIVED: 'Waived',
  TRIAL: 'Trial',
  ORDERED: 'Ordered',
  OVERDUE: 'Overdue',
  OFF: 'Off',
  STAGING: 'Staging',
  LIVE: 'Live',
  IN_PROGRESS: 'In Progress',
  DELIVERED: 'Delivered',
  SHIPPED: 'Shipped',
  RETURNED: 'Returned',
  REDEEMED: 'Redeemed',
  EXPIRED: 'Expired',
  CLEARED: 'Cleared',
  BOUNCED: 'Bounced',
  DEPOSITED: 'Deposited',
  OPEN: 'Open',
  OCCUPIED: 'Occupied',
  RESERVED: 'Reserved',
  AVAILABLE: 'Available',
  BILLED: 'Billed',
  CONFIRMED: 'Confirmed',
  NO_SHOW: 'No Show',
  'DINE-IN': 'Dine-in',
  DINE_IN: 'Dine-in',
  TAKEAWAY: 'Takeaway',
  DELIVERY: 'Delivery',
  CLOCK_IN: 'Clocked In',
  CLOCK_OUT: 'Clocked Out',
  REGULAR: 'Regular',
  VIP: 'VIP',
  WHOLESALE: 'Wholesale',
  IN_TRANSIT: 'In Transit',
};

const TONE_BY_STATUS = new Map<string, Tone>();
for (const [tone, values] of Object.entries(TONES) as [Tone, readonly string[]][]) {
  for (const v of values) TONE_BY_STATUS.set(v, tone);
}
// Domain extras that don't fit the grouped lists above
TONE_BY_STATUS.set('TAKEAWAY', 'warning');
TONE_BY_STATUS.set('DELIVERY', 'info');
TONE_BY_STATUS.set('VIP', 'info');
TONE_BY_STATUS.set('RETURNED', 'info');

function statusTone(status: string): Tone {
  return TONE_BY_STATUS.get(status) ?? 'outline';
}

function humanise(status: string): string {
  return LABELS[status] ?? status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

interface StatusBadgeProps {
  status: string;
  className?: string;
  tone?: Tone;
  /** Localized display text; tone still derives from `status`. Defaults to the English label. */
  label?: string;
  /** Optional decorative icon rendered before the label (size it at the call site, h-3/h-3.5). */
  icon?: React.ReactNode;
  /** Full-colour fill instead of the 15% tint, for high-visibility contexts (KDS board). */
  solid?: boolean;
}

export function StatusBadge({ status, className, tone, label, icon, solid }: StatusBadgeProps) {
  const key = (status ?? '').toUpperCase();
  if (!key) return <Badge variant="outline">—</Badge>;
  const resolved = tone ?? statusTone(key);
  return (
    <Badge
      variant={resolved}
      className={cn(icon ? 'gap-1' : undefined, solid ? SOLID_CLASSES[resolved] : undefined, className)}
    >
      {icon ? (
        <span aria-hidden="true" className="inline-flex shrink-0 items-center">
          {icon}
        </span>
      ) : null}
      {label ?? humanise(key)}
    </Badge>
  );
}
